import AsyncStorage from '@react-native-async-storage/async-storage';
import { User } from '../types/auth';
import { LedgerEntry } from '../types/ledger';

const STORAGE_KEYS = {
  USER: '@lnb_user',
  TOKEN: '@lnb_token',
  LEDGER_ENTRIES: '@lnb_ledger_entries',
};

export const storage = {
  // Save user data
  async saveUser(user: User): Promise<void> {
    try {
      await AsyncStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    } catch (error) {
      console.error('Error saving user:', error);
      throw error;
    }
  },

  // Get user data
  async getUser(): Promise<User | null> {
    try {
      const userData = await AsyncStorage.getItem(STORAGE_KEYS.USER);
      if (userData) {
        const user = JSON.parse(userData);
        // Validate and convert createdAt back to Date object
        if (user.createdAt) {
          user.createdAt = new Date(user.createdAt);
          // Check if date is valid
          if (isNaN(user.createdAt.getTime())) {
            console.error('Invalid createdAt date in stored user data');
            return null;
          }
        } else {
          console.error('Missing createdAt in stored user data');
          return null;
        }
        return user;
      }
      return null;
    } catch (error) {
      console.error('Error getting user:', error);
      return null;
    }
  },

  // Remove user data (logout)
  async removeUser(): Promise<void> {
    try {
      await AsyncStorage.removeItem(STORAGE_KEYS.USER);
    } catch (error) {
      console.error('Error removing user:', error);
      throw error;
    }
  },

  // Save auth token
  async saveToken(token: string): Promise<void> {
    try {
      await AsyncStorage.setItem(STORAGE_KEYS.TOKEN, token);
    } catch (error) {
      console.error('Error saving token:', error);
      throw error;
    }
  },

  // Raw stored token. Use getToken() for API calls so expiry can be refreshed.
  async getStoredToken(): Promise<string | null> {
    try {
      return await AsyncStorage.getItem(STORAGE_KEYS.TOKEN);
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  },

  // Get a valid auth token, refreshing from Firebase when the cached JWT is expired.
  async getToken(): Promise<string | null> {
    try {
      const { getValidAuthToken } = require('./authSession');
      return await getValidAuthToken();
    } catch (error) {
      console.error('Error getting valid auth token:', error);
      return this.getStoredToken();
    }
  },

  // Remove auth token
  async removeToken(): Promise<void> {
    try {
      await AsyncStorage.removeItem(STORAGE_KEYS.TOKEN);
    } catch (error) {
      console.error('Error removing token:', error);
      throw error;
    }
  },

  // Clear all storage
  async clearAll(): Promise<void> {
    try {
      await AsyncStorage.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
      throw error;
    }
  },

  // Save ledger entries
  async saveLedgerEntries(entries: LedgerEntry[]): Promise<void> {
    try {
      await AsyncStorage.setItem(STORAGE_KEYS.LEDGER_ENTRIES, JSON.stringify(entries));
    } catch (error) {
      console.error('Error saving ledger entries:', error);
      throw error;
    }
  },

  // Get ledger entries
  async getLedgerEntries(): Promise<LedgerEntry[]> {
    try {
      const entriesData = await AsyncStorage.getItem(STORAGE_KEYS.LEDGER_ENTRIES);
      if (entriesData) {
        const entries = JSON.parse(entriesData);
        // Convert date strings back to Date objects
        return entries.map((entry: any) => ({
          ...entry,
          date: new Date(entry.date),
          createdAt: new Date(entry.createdAt),
        }));
      }
      return [];
    } catch (error) {
      console.error('Error getting ledger entries:', error);
      return [];
    }
  },

  // Add a new ledger entry
  async addLedgerEntry(entry: LedgerEntry): Promise<void> {
    try {
      const entries = await this.getLedgerEntries();
      entries.push(entry);
      await this.saveLedgerEntries(entries);
    } catch (error) {
      console.error('Error adding ledger entry:', error);
      throw error;
    }
  },

  // Update an existing ledger entry
  async updateLedgerEntry(updatedEntry: LedgerEntry): Promise<void> {
    try {
      const entries = await this.getLedgerEntries();
      const index = entries.findIndex(e => e.id === updatedEntry.id);
      if (index !== -1) {
        entries[index] = updatedEntry;
        await this.saveLedgerEntries(entries);
      } else {
        throw new Error(`Entry with id ${updatedEntry.id} not found`);
      }
    } catch (error) {
      console.error('Error updating ledger entry:', error);
      throw error;
    }
  },

  // Delete a ledger entry
  async deleteLedgerEntry(entryId: string): Promise<void> {
    try {
      const entries = await this.getLedgerEntries();
      const filteredEntries = entries.filter(e => e.id !== entryId);
      await this.saveLedgerEntries(filteredEntries);
    } catch (error) {
      console.error('Error deleting ledger entry:', error);
      throw error;
    }
  },
};
